import { OperationOutcomeError } from "@medplum/core";

const PRECONDITION_FAILED_STATUS = 412;
const MULTIPLE_MATCHES_ISSUE_CODE = "multiple-matches";

/** Medplum stamps these ids on the canned outcomes it answers 412 with. */
const PRECONDITION_FAILED_OUTCOME_IDS = ["multiple-matches", "precondition-failed"];

interface ErrorWithStatus {
  status?: unknown;
  statusCode?: unknown;
  response?: {
    status?: unknown;
  };
}

function getStatusCode(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const maybeError = error as ErrorWithStatus;
  if (typeof maybeError.status === "number") {
    return maybeError.status;
  }

  if (typeof maybeError.statusCode === "number") {
    return maybeError.statusCode;
  }

  return typeof maybeError.response?.status === "number" ? maybeError.response.status : undefined;
}

/**
 * Detects a conditional request that the server refused with 412 Precondition Failed.
 *
 * Medplum answers `If-None-Exist` with 412 when the search matches more than one
 * resource. The `OperationOutcomeError` it throws carries no `status` field, so the
 * outcome identifies the failure; the status check covers plain HTTP error shapes.
 * The generic 412 outcome uses the catch-all `processing` issue code, which is why
 * this reads the outcome id instead.
 */
export function isMedplumPreconditionFailedError(error: unknown): boolean {
  if (error instanceof OperationOutcomeError) {
    const outcomeId = error.outcome?.id;
    if (outcomeId && PRECONDITION_FAILED_OUTCOME_IDS.includes(outcomeId)) {
      return true;
    }

    if (error.outcome?.issue?.[0]?.code === MULTIPLE_MATCHES_ISSUE_CODE) {
      return true;
    }
  }

  return getStatusCode(error) === PRECONDITION_FAILED_STATUS;
}
